import WifiOverridesService from '../../services/wifiOverrides';
import i18n from '../../i18n';

const namespaced = true;

const state = {
  overrides: [],
  total: 0,
  loading: false,
  filters: {
    bssid: '',
  },
  pagination: {
    page: 1,
    size: 25,
  },
};

const mutations = {
  setOverrides(state, { data, total }) {
    state.overrides = data;
    state.total = total;
  },
  setLoading(state, loading) {
    state.loading = loading;
  },
  setFilters(state, filters) {
    state.filters = { ...state.filters, ...filters };
  },
  setPagination(state, pagination) {
    state.pagination = { ...state.pagination, ...pagination };
  },
  clearFilters(state) {
    state.filters = { bssid: '' };
  },
};

const getters = {
  overrides: (state) => state.overrides,
  total: (state) => state.total,
  loading: (state) => state.loading,
  filters: (state) => state.filters,
  pagination: (state) => state.pagination,
};

const actions = {
  async fetchOverrides({ commit, state, rootGetters }, payload = {}) {
    commit('setLoading', true);
    try {
      const orgId = rootGetters['organization/currentOrganization']?.id;
      if (!orgId) {
        throw new Error('Organization ID required');
      }

      const fetchAll = Object.prototype.hasOwnProperty.call(payload || {}, 'fetchAll');
      const page = fetchAll ? 1 : state.pagination.page;
      const size = fetchAll ? 10000 : state.pagination.size;

      const response = await WifiOverridesService.list({
        orgId,
        page,
        size,
        bssid: state.filters.bssid,
        fetchAll
      });

      commit('setOverrides', {
        data: response.data,
        total: response.total,
      });

      return response;
    } catch (e) {
      let msg = e && e.message;
      throw new Error(msg || i18n.t('something-went-wrong'));
    } finally {
      commit('setLoading', false);
    }
  },

  async createOverride({ rootGetters }, { bssid, lat, lng }) {
    try {
      const orgId = rootGetters['organization/currentOrganization']?.id;
      if (!orgId) {
        throw new Error('Organization ID required');
      }

      const response = await WifiOverridesService.upsert({
        orgId,
        bssid,
        lat,
        lng,
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to create override');
    }
  },

  async updateOverride({ rootGetters }, { bssid, lat, lng }) {
    try {
      const orgId = rootGetters['organization/currentOrganization']?.id;
      if (!orgId) {
        throw new Error('Organization ID required');
      }
      const response = await WifiOverridesService.update({
        orgId,
        bssid,
        lat,
        lng,
      });

      return response;
    } catch (e) {
      let msg = e && e.message;
      throw new Error(msg || i18n.t('something-went-wrong'));
    }
  },

  async deleteOverride({ rootGetters }, { bssid }) {
    try {
      const orgId = rootGetters['organization/currentOrganization']?.id;
      if (!orgId) {
        throw new Error('Organization ID required');
      }

      const response = await WifiOverridesService.remove({
        orgId,
        bssid,
      });

      return response;
    } catch (e) {
      let msg = e && e.message;
      throw new Error(msg || i18n.t('something-went-wrong'));
    }
  },

  async bulkUpload({ rootGetters }, { file, dryRun }) {
    try {
      const orgId = rootGetters['organization/currentOrganization']?.id;
      if (!orgId) throw new Error('Organization ID required');
      if (!file) throw new Error('CSV file is required');

      const resp = await WifiOverridesService.bulkUpload({ orgId, file, dryRun });
      return resp;
    } catch (e) {
      throw e;
    }
  },

  updateFilters({ commit }, filters) {
    commit('setFilters', filters);
  },

  updatePagination({ commit }, pagination) {
    commit('setPagination', pagination);
  },

  clearFilters({ commit }) {
    commit('clearFilters');
  },
};

export default {
  namespaced,
  state,
  getters,
  mutations,
  actions,
};